import { useCallback, useEffect, useRef, useState } from 'react'
import { useAppDispatch } from '@/utils/typeHelpers'
import type { WidgetDisplay } from '../lib/types'
import {
  fetchMerchantReview,
  markMerchantPersonal,
  MERCHANT_GONE,
  type MerchantReview,
  type MerchantReviewTransaction,
} from '../merchantReview.slice'
import { plural } from '../lib/plural'
import { useMerchantCard } from './useMerchantCard'

interface RunTally {
  categorized: number
  personal: number
  locked: number
  stale: number
}

const toRunClause = ({ categorized, personal }: RunTally): string =>
  [
    categorized && `I categorized ${plural(categorized, 'merchant')}.`,
    personal && `I marked ${plural(personal, 'merchant')} as personal.`,
  ]
    .filter(Boolean)
    .join(' ')

const toRunLabel = ({ categorized, personal }: RunTally): string =>
  `${[
    categorized && `${plural(categorized, 'merchant')} categorized`,
    personal && `${plural(personal, 'merchant')} marked personal`,
  ]
    .filter(Boolean)
    .join(', ')}.`

const hasRun = (tally: RunTally): boolean =>
  tally.categorized + tally.personal > 0

const toHelpLabel = (tally: RunTally, merchant: string): string =>
  hasRun(tally)
    ? `${toRunLabel(tally)} Looking into ${merchant}.`
    : `Looking into ${merchant}.`

const toClosingSentence = (
  tally: RunTally,
  remaining: number | null
): string => {
  const tail =
    remaining === null || remaining === 0
      ? 'That was the last of them.'
      : `${plural(remaining, 'merchant')} still to go.`

  const clauses = [toRunClause(tally)]
  if (tally.locked) {
    clauses.push(`Leave the ${tally.locked} in closed books alone.`)
  }
  if (tally.stale) {
    clauses.push(`${tally.stale} had already moved on.`)
  }
  clauses.push(tail)
  return clauses.filter(Boolean).join(' ')
}

export interface MerchantCategorization {
  group?: MerchantReview
  progress: { position: number; total: number } | null
  groupCategoryId: number | null
  primaryLabel: string
  nameOf: (categoryId: number | null) => string
  categoryOf: (transaction: MerchantReviewTransaction) => number | null
  setGroupCategory: (categoryId: number | null) => void
  setRowCategory: (transactionId: number, categoryId: number | null) => void
  isRowPersonal: (transactionId: number) => boolean
  toggleRowPersonal: (transactionId: number) => void
  canSave: boolean
  submit: () => Promise<void>
  markPersonal: () => Promise<void>
  saving: boolean
  askForHelp: () => void
}

export const useMerchantCategorization = (
  groupKeys: string[],
  onOutcome: (text: string, display?: WidgetDisplay) => void
): MerchantCategorization => {
  const dispatch = useAppDispatch()
  const [index, setIndex] = useState<number | null>(null)
  const card = useMerchantCard(index === null ? '' : (groupKeys[index] ?? ''))

  const tally = useRef<RunTally>({
    categorized: 0,
    personal: 0,
    locked: 0,
    stale: 0,
  })
  const remaining = useRef<number | null>(null)
  const saved = useRef<number | null>(null)
  const { setSaving } = card

  const openFrom = useCallback(
    async (from: number): Promise<void> => {
      for (let next = from; next < groupKeys.length; next += 1) {
        const loaded = await dispatch(fetchMerchantReview(groupKeys[next]))
        if (loaded === MERCHANT_GONE) continue
        if (!loaded) {
          saved.current = next
          setSaving(false)
          return
        }
        saved.current = null
        setIndex(next)
        setSaving(false)
        return
      }
      saved.current = null
      setIndex(null)
      onOutcome(toClosingSentence(tally.current, remaining.current), {
        label: hasRun(tally.current)
          ? toRunLabel(tally.current)
          : 'Nothing left to categorize.',
      })
    },
    [dispatch, groupKeys, onOutcome, setSaving]
  )

  const opened = useRef(false)
  useEffect(() => {
    if (opened.current) return
    opened.current = true
    void openFrom(0)
  }, [openFrom])

  const advanceAfterSave = async (
    categoryFor: (transaction: MerchantReviewTransaction) => number | null
  ): Promise<void> => {
    if (index === null) return
    if (saved.current !== null) {
      setSaving(true)
      await openFrom(saved.current)
      return
    }

    const result = await card.save(categoryFor)
    if (!result) return

    if (result.saved.length > 0) tally.current.categorized += 1
    else if (result.personalCount > 0) tally.current.personal += 1
    tally.current.locked += result.lockedCount
    tally.current.stale += result.staleCount
    remaining.current = result.merchantsRemaining
    await openFrom(index + 1)
  }

  const markPersonal = async (): Promise<void> => {
    if (index === null || !card.group || card.saving) return
    setSaving(true)
    if (saved.current !== null) {
      await openFrom(saved.current)
      return
    }

    const result = await dispatch(
      markMerchantPersonal(
        card.group.groupKey,
        card.group.transactions.map((transaction) => transaction.id)
      )
    )
    if (result === MERCHANT_GONE) {
      await openFrom(index + 1)
      return
    }
    if (!result) {
      setSaving(false)
      return
    }

    if (result.transactionCount > 0) tally.current.personal += 1
    tally.current.locked += result.lockedCount
    remaining.current = result.merchantsRemaining
    await openFrom(index + 1)
  }

  return {
    group: card.group,
    progress:
      index === null ? null : { position: index + 1, total: groupKeys.length },
    groupCategoryId: card.groupCategoryId,
    primaryLabel: card.primaryLabel,
    nameOf: card.nameOf,
    categoryOf: card.categoryOf,
    setGroupCategory: card.setGroupCategory,
    setRowCategory: card.setRowCategory,
    isRowPersonal: card.isRowPersonal,
    toggleRowPersonal: card.toggleRowPersonal,
    canSave: card.canSave,
    submit: (): Promise<void> => advanceAfterSave(card.categoryOf),
    markPersonal,
    saving: card.saving,
    askForHelp: (): void => {
      if (!card.group) return
      const run = toRunClause(tally.current)
      onOutcome(run ? `${run} ${card.unsureSentence}` : card.unsureSentence, {
        label: toHelpLabel(tally.current, card.group.merchant),
      })
    },
  }
}
